/** @format */

import { Box, SwipeableDrawer } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { formatBytes } from 'bytes-formatter';
import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;
import Slider from 'react-slick';
import { useTranslation } from 'react-i18next';
import { CrossIcon, DownloadIcon } from 'src/components/icons';
import { useAnimatedNumber } from 'src/hooks/useAnimatedNumber';
import useDeviceDetect from 'src/hooks/useDeviceDetect';
import { PDF_VIEWER_OPEN_CLASS } from './constants';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const fallbackViewerDoc = { title: '', size: 0, url: '', mimeType: '' };
const ESC_CLOSE_DELAY_MS = 100;
const DESKTOP_PAGE_HEIGHT = 600;
const DESKTOP_THUMB_HEIGHT = 100;

function decodeFileName(value) {
  if (value == null) return '';
  const text = String(value).trim();
  if (!text) return '';
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

function isPdfDocument(file) {
  if (!file || typeof file !== 'object') return false;
  const fileName = String(file.title || '').toLowerCase();
  const mimeType = String(file.mimeType || '').toLowerCase();
  return mimeType.includes('application/pdf') || fileName.endsWith('.pdf');
}

function toProgressPercent(loaded, total) {
  if (!total || total <= 0) return null;
  return Math.min(100, Math.max(0, Math.round((loaded / total) * 100)));
}

export async function fetchPdfBlobWithProgress(url, { signal, onProgress, expectedSize = 0 } = {}) {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Failed to load pdf: ${response.status}`);
  }

  const total = Number(response.headers.get('content-length')) || Number(expectedSize) || 0;
  if (!response.body || typeof response.body.getReader !== 'function') {
    onProgress?.(total > 0 ? 100 : null);
    return response.blob();
  }

  const reader = response.body.getReader();
  const chunks = [];
  let loaded = 0;

  onProgress?.(total > 0 ? 0 : null);

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress?.(toProgressPercent(loaded, total));
  }

  if (total > 0) onProgress?.(100);
  return new Blob(chunks, { type: response.headers.get('content-type') || 'application/pdf' });
}

function useElementWidth(enabled) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setWidth(0);
      return undefined;
    }

    let cancelled = false;
    let observer = null;
    const rafId = window.requestAnimationFrame(() => {
      if (cancelled) return;

      const node = ref.current;
      if (!node) {
        setWidth(Math.max(0, window.innerWidth - 32));
        return;
      }

      const update = () => setWidth(Math.max(0, Math.floor(node.getBoundingClientRect().width)));
      update();

      if (typeof ResizeObserver === 'undefined') return;
      observer = new ResizeObserver(update);
      observer.observe(node);
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
      observer?.disconnect();
    };
  }, [enabled]);

  return { ref, width };
}

function PDFViewerSkeleton({ progress = null, isMobile = false }) {
  const { t } = useTranslation();
  const animatedProgress = useAnimatedNumber(progress);
  const showPercent = typeof animatedProgress === 'number';
  const progressLabel = showPercent
    ? t('extranet.pdfViewer.loadingPercent', { percent: animatedProgress })
    : '';

  return (
    <div
      className={`pdf-viewer-skeleton${isMobile ? ' pdf-viewer-skeleton--mobile' : ''}`}
      aria-busy="true">
      <div className="pdf-viewer-skeleton__pages">
        <Skeleton variant="rectangular" className="pdf-viewer-skeleton__page" height={isMobile ? 420 : 600} />
        {!isMobile ? (
          <Skeleton variant="rectangular" className="pdf-viewer-skeleton__page" height={600} />
        ) : null}
        {showPercent ? (
          <div
            className="pdf-viewer-skeleton__progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={animatedProgress}
            aria-label={progressLabel || undefined}>
            <span className="pdf-viewer-skeleton__progress-value">{animatedProgress}%</span>
          </div>
        ) : null}
      </div>
      {!isMobile ? (
        <>
          <div className="pdf-viewer-skeleton__thumbs">
            {Array.from({ length: 10 }).map((_, index) => (
              <Skeleton key={index} variant="rectangular" className="pdf-viewer-skeleton__thumb" height={96} />
            ))}
          </div>
          <div className="pdf-viewer-skeleton__arrows">
            <Skeleton variant="circular" className="pdf-viewer-skeleton__arrow" width={48} height={48} />
            <Skeleton variant="circular" className="pdf-viewer-skeleton__arrow" width={48} height={48} />
          </div>
        </>
      ) : null}
    </div>
  );
}

function PDFPagesStack({ numPages, pageWidth }) {
  return (
    <div className="pdf-viewer__pages-stack">
      {numPages > 0 &&
        Array.from({ length: numPages }).map((_item, i) => (
          <div key={i} className="pdf-viewer__page-item">
            <Page
              width={pageWidth > 0 ? pageWidth : undefined}
              pageNumber={i + 1}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              loading={<div className="pdf-viewer__page-loading pdf-viewer__page-loading--stack" />}
            />
          </div>
        ))}
    </div>
  );
}

function PDFPagesCarousel({ numPages, pageNumber, setPageNumber, slide1, slide2 }) {
  const settingsImage = {
    asNavFor: slide2.current,
    dots: false,
    speed: 500,
    arrows: false,
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    adaptiveHeight: false,
    variableWidth: true,
    beforeChange: (_current, next) => {
      setPageNumber(next);
    }
  };

  const settingPagination = {
    asNavFor: slide1.current,
    arrows: false,
    slidesToShow: 16,
    slidesToScroll: 1,
    swipeToSlide: true,
    focusOnSelect: true,
    infinite: false
  };

  return (
    <>
      <Slider {...settingsImage} className="slide1 pdf-viewer__main-slider" ref={slide1}>
        {numPages > 0 &&
          Array.from({ length: numPages }).map((_item, i) => (
            <Box key={i} className="onboard-image">
              <Page
                height={DESKTOP_PAGE_HEIGHT}
                pageNumber={i + 1}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                loading={<div className="pdf-viewer__page-loading pdf-viewer__page-loading--main" />}
              />
            </Box>
          ))}
      </Slider>

      <Slider ref={slide2} {...settingPagination} className="slide1 preview-pdf-pagi pdf-viewer__thumb-slider">
        {numPages > 0 &&
          Array.from({ length: numPages }).map((_item, i) => (
            <Box key={i} className="onboard-image">
              <Page
                height={DESKTOP_THUMB_HEIGHT}
                pageNumber={i + 1}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                loading={<div className="pdf-viewer__page-loading pdf-viewer__page-loading--thumb" />}
              />
            </Box>
          ))}
      </Slider>
      <div className="custom-arrow">
        <button
          type="button"
          className="custom-arrow__button"
          disabled={pageNumber === 0}
          onClick={() => slide2.current?.slickPrev()}>
          &#8249;
        </button>
        <button
          type="button"
          className="custom-arrow__button"
          disabled={numPages < 1 || pageNumber === numPages - 1}
          onClick={() => slide2.current?.slickNext()}>
          &#8250;
        </button>
      </div>
    </>
  );
}

export default function PDFViewer({ openState, toggleDrawer, currentDoc, downloadFile }) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceDetect();
  const [blob, setBlob] = useState(null);
  const [loadProgress, setLoadProgress] = useState(null);
  const controller = useRef(null);
  const escCloseTimerRef = useRef(null);
  const slide1 = useRef(null);
  const slide2 = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const { ref: stackWidthRef, width: stackWidth } = useElementWidth(isMobile && openState);
  const viewerDoc = currentDoc || fallbackViewerDoc;
  const decodedTitle = decodeFileName(viewerDoc.title);
  const skeleton = <PDFViewerSkeleton progress={loadProgress} isMobile={isMobile} />;

  useEffect(() => {
    if (!openState) return undefined;

    const handleKeyDown = event => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      if (escCloseTimerRef.current) return;
      escCloseTimerRef.current = window.setTimeout(() => {
        escCloseTimerRef.current = null;
        toggleDrawer?.();
      }, ESC_CLOSE_DELAY_MS);
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      if (escCloseTimerRef.current) {
        clearTimeout(escCloseTimerRef.current);
        escCloseTimerRef.current = null;
      }
    };
  }, [openState, toggleDrawer]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (openState) document.body.classList.add(PDF_VIEWER_OPEN_CLASS);
    else document.body.classList.remove(PDF_VIEWER_OPEN_CLASS);
    return () => {
      document.body.classList.remove(PDF_VIEWER_OPEN_CLASS);
    };
  }, [openState]);

  useEffect(() => {
    setBlob(null);
    setNumPages(0);
    setPageNumber(0);
    setLoadProgress(null);

    if (!viewerDoc.url || !isPdfDocument(viewerDoc)) {
      return undefined;
    }

    controller.current = new AbortController();
    const signal = controller.current.signal;

    fetchPdfBlobWithProgress(viewerDoc.url, {
      signal,
      expectedSize: viewerDoc.size,
      onProgress: setLoadProgress
    })
      .then(fileBlob => setBlob(fileBlob))
      .catch(error => {
        if (error.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      });

    return () => {
      if (controller.current?.signal && process.env.NODE_ENV !== 'development') {
        controller.current.abort('aborted');
      }
    };
  }, [viewerDoc]);

  function onDocumentLoadSuccess({ numPages: loadedPages }) {
    setNumPages(loadedPages);
    setLoadProgress(100);
  }

  function onDocumentLoadProgress({ loaded, total }) {
    const percent = toProgressPercent(loaded, total);
    if (percent != null) setLoadProgress(percent);
  }

  return (
    <SwipeableDrawer
      anchor="right"
      open={openState}
      onClose={toggleDrawer}
      onOpen={() => {}}
      PaperProps={{
        className: `custom-swipeable${isMobile ? ' custom-swipeable--pdf-mobile' : ''}`,
        style: { width: '100%', borderRadius: 0 }
      }}
      ModalProps={{ style: { zIndex: 6000 } }}>
      <div className={`pdf-viewer-wrapper${isMobile ? ' pdf-viewer-wrapper--mobile' : ''}`}>
        <button onClick={toggleDrawer} className="close-drawer" type="button">
          <span>{t('common.button.close')}</span>
          <CrossIcon width={18} height={18} />
        </button>
        <div
          className={`pdf-viewer-container${isMobile ? ' pdf-viewer-container--mobile' : ''}`}
          ref={isMobile ? stackWidthRef : undefined}>
          <div className="pdf-viewer-top">
            <div className="title">{decodedTitle || viewerDoc.title}</div>
            <div className="type-size">
              <div className="type">
                {String(decodedTitle || viewerDoc.title || '')
                  .split('.')
                  .pop() || 'PDF'}
              </div>
              <div className="size">{formatBytes(viewerDoc.size || 0)}</div>
            </div>
            <button
              className="download-btn pdf-viewer__download-btn"
              type="button"
              onClick={() => {
                if (typeof downloadFile === 'function') downloadFile(viewerDoc);
              }}>
              <span>{t('extranet.pdfViewer.download')}</span>
              {DownloadIcon ? <DownloadIcon /> : null}
            </button>
          </div>
          <Document
            file={blob}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadProgress={onDocumentLoadProgress}
            onLoadError={console.error}
            loading={skeleton}
            noData={skeleton}>
            {isMobile ? (
              <PDFPagesStack numPages={numPages} pageWidth={stackWidth} />
            ) : (
              <PDFPagesCarousel
                numPages={numPages}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                slide1={slide1}
                slide2={slide2}
              />
            )}
          </Document>
        </div>
      </div>
    </SwipeableDrawer>
  );
}
